import { Fragment, useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { parseInstant } from "../../utils/dateUtils";
import { adminService } from "../../features/admin/service";
import Pagination from "../../components/admin/Pagination";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";

const sectionSpinner = (
  <div className="flex items-center justify-center py-10 text-gray-500 text-sm"><Spinner /></div>
);

type VoiceAgentLog = {
  id: string;
  call_type: string;
  endpoint: string;
  request_payload: Record<string, unknown>;
  status: string;
  response_body: Record<string, unknown> | null;
  error: string | null;
  campaign_id: string | null;
  phone_last4: string | null;
  delivery_status: string | null;
  delivery_updated_at: string | null;
  created_at: string | null;
};

const STATUS_STYLES: Record<string, string> = {
  completed: "bg-emerald-500/10 text-emerald-400",
  failed: "bg-red-500/10 text-red-400",
};

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  const d = parseInstant(iso) ?? new Date(iso);
  return d.toLocaleString();
}


const QUEUE_STATUS_STYLES: Record<string, string> = {
  pending: "bg-amber-500/10 text-amber-400",
  dialing: "bg-blue-500/10 text-blue-400",
  sent: "bg-emerald-500/10 text-emerald-400",
  expired: "bg-gray-500/10 text-gray-400",
  failed: "bg-red-500/10 text-red-400",
};

const AdminVoiceAgentPage: React.FC = () => {
  const [summary, setSummary] = useState<{ completed: number; failed: number; total: number } | null>(null);
  const [logs, setLogs] = useState<VoiceAgentLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState("");
  const { showError } = useAdminToast();
  useEffect(() => { if (logsError) showError(logsError); }, [logsError, showError]);
  const [statusFilter, setStatusFilter] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(8);

  type VoiceAgentConfig = Awaited<ReturnType<typeof adminService.getVoiceAgentConfig>>;
  const [config, setConfig] = useState<VoiceAgentConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [togglePending, setTogglePending] = useState(false);

  const checkConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      setConfig(await adminService.getVoiceAgentConfig());
    } catch (err) {
      console.error(err);
      setConfig({ configured: false, webhook_url: null, api_key_set: false });
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const callsEnabled = config?.calls?.enabled ?? true;

  const handleToggleCalls = useCallback(async () => {
    // Turning calling ON starts phoning real people on the next enquiry, so it
    // asks first. Turning it OFF is the safe direction and goes straight
    // through — the whole point of the switch is stopping calls quickly.
    if (!callsEnabled) {
      const ok = window.confirm(
        "Turn calling on?\n\nFrom the next enquiry onwards, Sophia will phone the seller of the deal that was enquired about.",
      );
      if (!ok) return;
    }
    setTogglePending(true);
    try {
      await adminService.setVoiceAgentCallsEnabled(!callsEnabled);
      await checkConfig();
    } catch (err) {
      console.error(err);
      window.alert("Could not change the setting. Nothing was altered — try again.");
    } finally {
      setTogglePending(false);
    }
  }, [callsEnabled, checkConfig]);

  useEffect(() => { void checkConfig(); }, [checkConfig]);

  type QueuedLead = Awaited<ReturnType<typeof adminService.getVoiceAgentQueuedLeads>>["items"][number];
  const [queued, setQueued] = useState<QueuedLead[]>([]);
  const [queuedCounts, setQueuedCounts] = useState<Record<string, number>>({});
  const [queuedLoading, setQueuedLoading] = useState(true);

  const loadQueued = useCallback(async () => {
    setQueuedLoading(true);
    try {
      // Pending first is what an operator actually wants: what is still owed a
      // call. Sent and expired rows are history and stay below.
      const res = await adminService.getVoiceAgentQueuedLeads({ limit: 25 });
      setQueued(res.items);
      setQueuedCounts(res.counts || {});
    } catch (err) {
      console.error(err);
      setQueued([]);
    } finally {
      setQueuedLoading(false);
    }
  }, []);

  useEffect(() => { void loadQueued(); }, [loadQueued]);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    setLogsError("");
    try {
      const [logsRes, summaryRes] = await Promise.all([
        adminService.getVoiceAgentLogs({
          status: statusFilter || undefined,
          limit: pageSize,
          skip: page * pageSize,
        }),
        adminService.getVoiceAgentLogsSummary(),
      ]);
      setLogs(logsRes.items);
      setTotal(logsRes.total);
      setSummary(summaryRes);
    } catch (err) {
      console.error(err);
      setLogsError("Failed to load voice agent call logs");
    } finally {
      setLogsLoading(false);
    }
  }, [statusFilter, page, pageSize]);

  useEffect(() => { void loadLogs(); }, [loadLogs]);
  useEffect(() => { setPage(0); }, [pageSize]);

  const handleRefreshAll = useCallback(() => {
    return Promise.all([checkConfig(), loadLogs(), loadQueued()]).then(() => {});
  }, [checkConfig, loadLogs, loadQueued]);

  return (
    <div className="p-6 max-w-5xl">
      <AdminPageHeader
        title="Voice Agent Call Log"
        subtitle="Monitor every enquiry/lead notification pushed to the voice agent's webhook."
        actions={<RefreshButton onRefresh={handleRefreshAll} />}
      />

      {/* Runtime on/off switch. Above the config card because it is the control
          an operator comes here to use; the card below is diagnostics. */}
      <div
        className={`mb-4 flex items-center gap-4 rounded-xl px-4 py-3 max-w-2xl border ${
          callsEnabled
            ? "bg-emerald-950/40 border-emerald-900"
            : "bg-gray-900 border-gray-800"
        }`}
      >
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-100">
            Calling {callsEnabled ? "is on" : "is off"}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {callsEnabled
              ? "Every new enquiry phones the seller of that deal."
              : "Enquiries are recorded as usual, but no calls are placed."}
          </p>
          {config?.calls?.updated_by && (
            <p className="text-xs text-gray-500 mt-1">
              Last changed by {config.calls.updated_by}
              {/* fmtDate, not new Date(): this app rewrites every datetime in a
                  JSON response to "31 Aug 2026, 05:05 PM IST" (see
                  core/response_datetime.py), which new Date() cannot parse —
                  the card read "Invalid Date" in production. fmtDate's
                  parseInstant handles that format. */}
              {config.calls.updated_at ? ` on ${fmtDate(config.calls.updated_at)}` : ""}
            </p>
          )}
          {/* Switched on but nothing to call with — otherwise this reads as
              working when every enquiry is silently failing. */}
          {callsEnabled && config && !config.configured && (
            <p className="text-xs text-amber-400 mt-1">
              No bot is configured on this deployment, so no calls will be placed
              even though the switch is on.
            </p>
          )}
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={callsEnabled}
          aria-label="Enquiry calling"
          onClick={handleToggleCalls}
          disabled={togglePending || configLoading}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 ${
            callsEnabled ? "bg-emerald-500" : "bg-gray-700"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              callsEnabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      {/* Voice agent webhook config status */}
      <div className="mb-8 flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 max-w-2xl">
        <span
          className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${
            configLoading ? "bg-gray-500 animate-pulse" : config?.configured ? "bg-emerald-500" : "bg-red-500"
          }`}
        />
        <div className="flex-1">
          <p className="text-sm text-gray-300">
            Voice agent: {configLoading ? "checking…" : config?.configured ? "configured" : "not configured"}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 break-all">
            {config?.voice_agent_base_url || "no address set"}{" "}
            <span className="text-gray-600">
              ({config?.api_key_set ? "API key set" : "no API key"}
              {config?.transport ? `, ${config.transport}` : ""})
            </span>
          </p>
          {config && !config.configured && (
            <p className="text-xs text-red-400 mt-0.5">
              Set VOICE_AGENT_BOT_ID and VOICE_AGENT_API_KEY in this
              environment's config to enable calling.
            </p>
          )}
          {config?.configured && !config.api_key_set && (
            <p className="text-xs text-red-400 mt-0.5">
              No API key — the voice agent will refuse every call with 401.
            </p>
          )}
        </div>
        <button
          onClick={checkConfig}
          disabled={configLoading}
          className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-700 text-gray-300 hover:text-white disabled:opacity-50"
        >
          Recheck
        </button>
      </div>

      {/* Queued leads — enquiries the voice agent refused because they arrived
          outside the 09:00-21:00 calling window. Deliberately its own section
          rather than rows in the call log: these are not failures, they are
          calls still owed, and mixing them with real errors hides both. */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-white">Queued Leads</h2>
          <div className="flex gap-2 text-xs">
            {(["pending", "sent", "expired", "failed"] as const).map((k) =>
              queuedCounts[k] ? (
                <span key={k} className={`px-2 py-1 rounded ${QUEUE_STATUS_STYLES[k]}`}>
                  {k} {queuedCounts[k]}
                </span>
              ) : null,
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Enquiries that arrived outside the 09:00&ndash;21:00 calling window. They
          are dialled automatically once it reopens, and dropped after 24 hours.
        </p>

        {queuedLoading ? (
          sectionSpinner
        ) : queued.length === 0 ? (
          <div className="rounded-xl border border-gray-800 bg-gray-900/40 px-4 py-8 text-center text-sm text-gray-500">
            Nothing queued. Enquiries are being called as they arrive.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-900/60 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Queued</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Seller</th>
                  <th className="px-4 py-3 text-left font-medium">Deal</th>
                  <th className="px-4 py-3 text-left font-medium">Calls after</th>
                </tr>
              </thead>
              <tbody>
                {queued.map((q) => (
                  <tr key={q.id} className="border-t border-gray-800">
                    <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{fmtDate(q.queued_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${QUEUE_STATUS_STYLES[q.status] || "bg-gray-500/10 text-gray-400"}`}>
                        {q.status}
                      </span>
                      {q.attempts > 1 && (
                        <span className="ml-2 text-xs text-gray-500">{q.attempts} attempts</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{q.seller}</td>
                    <td className="px-4 py-3 text-gray-400 max-w-xs truncate" title={q.deal}>{q.deal}</td>
                    <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                      {q.status === "pending" ? fmtDate(q.dial_after) : "—"}
                      {q.last_error && (
                        <div className="text-xs text-red-400 mt-0.5 max-w-xs truncate" title={q.last_error}>
                          {q.last_error}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Call logs */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-white">Voice Agent Webhook Call Log</h2>
          <div className="flex gap-2">
            {(["", "completed", "failed"] as const).map((s) => (
              <button
                key={s || "all"}
                onClick={() => { setStatusFilter(s); setPage(0); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                  statusFilter === s
                    ? "bg-indigo-600 border-indigo-600 text-white"
                    : "bg-gray-900 border-gray-800 text-gray-400 hover:text-white"
                }`}
              >
                {s === "" ? "All" : s === "completed" ? "Completed" : "Failed"}
                {summary && s !== "" && ` (${s === "completed" ? summary.completed : summary.failed})`}
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Every enquiry/lead pushed to the voice agent — status and the exact payload sent (deal + seller details).
        </p>

        {logsError && <p className="text-sm text-red-400 mb-3">{logsError}</p>}

        {logsLoading ? sectionSpinner : (
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-left text-gray-500 text-xs uppercase">
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Buyer</th>
                  <th className="px-4 py-3">Deal</th>
                  <th className="px-4 py-3">Endpoint</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">No calls logged yet.</td>
                  </tr>
                )}
                {logs.map((log) => {
                  const payload = log.request_payload || {};
                  const dealTitle = String(payload.dealTitle ?? payload.dealId ?? "—");
                  const buyer = (payload.buyer ?? {}) as Record<string, unknown>;
                  const buyerLabel = String(buyer.name ?? buyer.phone ?? buyer.email ?? "—");
                  return (
                    <Fragment key={log.id}>
                      <tr
                        onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                        className="border-b border-gray-800 last:border-0 cursor-pointer hover:bg-gray-800/50"
                      >
                        <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{fmtDate(log.created_at)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[log.status] ?? "bg-gray-700 text-gray-300"}`}>
                            {log.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{buyerLabel}</td>
                        <td className="px-4 py-3 text-gray-300 max-w-xs truncate">{dealTitle}</td>
                        <td className="px-4 py-3 text-gray-400 max-w-xs truncate">{log.endpoint}</td>
                        <td className="px-4 py-3 text-gray-500">{expanded === log.id ? "▲" : "▼"}</td>
                      </tr>
                      {expanded === log.id && (
                        <tr className="border-b border-gray-800 last:border-0 bg-gray-950">
                          <td colSpan={6} className="px-4 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Endpoint (full URL called)</p>
                                <p className="text-xs text-gray-300 break-all mb-3">{log.endpoint}</p>
                                <p className="text-xs text-gray-500 mb-1">Buyer</p>
                                <p className="text-xs text-gray-300 mb-3">
                                  {buyer.name ? String(buyer.name) : "—"}
                                  {buyer.phone ? ` · ${String(buyer.phone)}` : ""}
                                  {buyer.email ? ` · ${String(buyer.email)}` : ""}
                                </p>
                                <p className="text-xs text-gray-500 mb-1">Data sent (buyer shown above is admin-only, never sent to the voice agent)</p>
                                <pre className="text-xs text-gray-300 bg-gray-900 border border-gray-800 rounded-lg p-3 overflow-x-auto">
                                  {JSON.stringify(
                                    Object.fromEntries(Object.entries(log.request_payload || {}).filter(([k]) => k !== "buyer")),
                                    null,
                                    2,
                                  )}
                                </pre>
                              </div>
                              <div>
                                {log.error && (
                                  <>
                                    <p className="text-xs text-red-400 mb-1">Error</p>
                                    <p className="text-xs text-red-300 mb-3">{log.error}</p>
                                  </>
                                )}
                                <p className="text-xs text-gray-500 mb-1">Response</p>
                                <pre className="text-xs text-gray-300 bg-gray-900 border border-gray-800 rounded-lg p-3 overflow-x-auto">
                                  {JSON.stringify(log.response_body ?? {}, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            <div className="border-t border-gray-800">
              <Pagination
                variant="panel"
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminVoiceAgentPage;
